import React, { useEffect, useState, useCallback } from 'react';
import { Compass, Clock, HeartPulse, BatteryLow, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

/**
 * BehaviorCheckinCard — the FOLLOW-THROUGH check-in (Nikki's COM-B loop made
 * visible). The permanent companion to the Mind & Body Check-in:
 *   Mind & Body  → how you FEEL (state)
 *   Behaviour    → how you FOLLOW THROUGH (friction)
 *
 * Three states (always shows one):
 *   ASK       — a miss was detected yesterday → "what got in the way?" + chips
 *   ON TRACK  — nothing to ask → a calm "On track" note
 *   PATTERN   — once ≥2 reasons logged → a one-line 30-day blocker pattern at
 *               the bottom (its single visible home)
 *
 * Reuses GET /api/wellness/behavior + POST /api/wellness/barrier. Fires
 * `wellness:refresh` after a save so the Daily Read + IQ refetch.
 */

const OPTION_ICONS = { opportunity: Clock, capability: HeartPulse, motivation: BatteryLow };
const SPLIT_META = {
  capability:  { label: 'Body',       color: '#22d3ee' },
  opportunity: { label: 'Time',       color: '#CCFF00' },
  motivation:  { label: 'Motivation', color: '#fb923c' },
};

const BehaviorCheckinCard = () => {
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [picked, setPicked] = useState(null);   // chosen barrier → show sub-reason step

  const load = useCallback(async () => {
    try {
      const { data } = await api.getBehaviorCheckin();
      setPack(data);
    } catch {
      setPack(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener('wellness:refresh', onRefresh);
    return () => window.removeEventListener('wellness:refresh', onRefresh);
  }, [load]);

  const answer = async (barrier, subReason = null) => {
    const probe = pack?.active_probe;
    if (!probe) return;
    setSaving(subReason || barrier);
    try {
      await api.reportBarrier({
        behavior: probe.behavior,
        barrier: barrier === 'skip' ? null : barrier,
        sub_reason: subReason,
        dismissed: barrier === 'skip',
      });
      if (barrier !== 'skip') {
        toast.success("Got it — I'll tune today around that.");
      }
      setPicked(null);
      window.dispatchEvent(new Event('wellness:refresh'));
      await load();
    } catch {
      toast.error('Could not save that — try again.');
    } finally {
      setSaving(null);
    }
  };

  const probe = pack?.active_probe;
  const pattern = pack?.pattern;
  const patternV2 = pack?.pattern_v2;
  const subReasons = (pack?.sub_reasons || {})[picked] || [];
  const recentResolved = pack?.recent_resolved;
  const hasPattern = (patternV2 && patternV2.total >= 1) || (pattern && pattern.total >= 1);

  return (
    <div className="hpme-card p-6 overflow-hidden flex flex-col" data-testid="behavior-checkin-card">
      <div className="hpme-card-corner" />

      <div className="flex items-center gap-2 mb-1">
        <Compass className="w-4 h-4 text-amber-300" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-secondary font-bold">
          Behaviour Check-in
        </span>
      </div>
      <p className="text-xs text-zinc-500 mb-4">How you follow through</p>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 text-sm py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Reading your week…
        </div>
      ) : probe ? (
        /* ── ASK state ── */
        <div className="space-y-3 flex-1" data-testid="behavior-checkin-ask">
          <p className="text-sm text-zinc-100 leading-relaxed" data-testid="behavior-checkin-question">
            {picked ? 'Got it — anything more specific?' : probe.question}
          </p>
          {!picked ? (
            <>
              <div className="flex flex-col gap-2">
                {(probe.options || []).map(opt => {
                  const Icon = OPTION_ICONS[opt.id] || Compass;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={!!saving}
                      onClick={() => setPicked(opt.id)}
                      className="group flex items-center gap-2.5 px-3 py-2.5 rounded-sm border border-zinc-700 bg-zinc-900/60 hover:border-amber-400 hover:bg-amber-400/10 hover:text-amber-200 transition-all text-sm text-left disabled:opacity-50"
                      data-testid={`behavior-checkin-opt-${opt.id}`}
                    >
                      <Icon className="w-4 h-4 shrink-0 text-zinc-400 group-hover:text-amber-300" />
                      <span>{opt.label}</span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                disabled={!!saving}
                onClick={() => answer('skip')}
                className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors disabled:opacity-50"
                data-testid="behavior-checkin-skip"
              >
                Skip for now
              </button>
            </>
          ) : (
            /* ── sub-reason step (Phase 5) ── */
            <div className="space-y-2" data-testid="behavior-checkin-subreasons">
              <div className="flex flex-wrap gap-2">
                {subReasons.map(sr => (
                  <button
                    key={sr.id}
                    type="button"
                    disabled={!!saving}
                    onClick={() => answer(picked, sr.id)}
                    className="px-3 py-1.5 rounded-sm border border-zinc-700 bg-zinc-900/60 hover:border-amber-400 hover:bg-amber-400/10 hover:text-amber-200 transition-all text-xs font-secondary font-bold uppercase tracking-wider disabled:opacity-50"
                    data-testid={`behavior-checkin-sub-${sr.id}`}
                  >
                    {saving === sr.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : sr.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3 pt-1">
                <button type="button" disabled={!!saving} onClick={() => answer(picked)}
                  className="text-xs text-amber-300 hover:text-amber-200 font-semibold disabled:opacity-50" data-testid="behavior-checkin-sub-save">
                  Save without detail
                </button>
                <button type="button" disabled={!!saving} onClick={() => setPicked(null)}
                  className="text-xs text-zinc-500 hover:text-zinc-300" data-testid="behavior-checkin-sub-back">
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ── RESOLVED / ON TRACK state ── reflects what actually happened, no false "all good" ── */
        <div className="flex-1 flex flex-col justify-center py-2" data-testid="behavior-checkin-ontrack">
          {recentResolved ? (
            <div className="flex items-center gap-2.5">
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-amber-400/10 border border-amber-400/30">
                <Check className="w-4.5 h-4.5 text-amber-300" />
              </span>
              <div>
                <p className="text-sm font-secondary font-bold text-white">Noted, not nagging</p>
                <p className="text-xs text-zinc-400">
                  You missed {recentResolved.behavior_label}
                  {recentResolved.barrier_label ? <> — <span className="text-amber-200">{recentResolved.barrier_label}</span> got in the way</> : null}. Totally fine — fresh start today.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-emerald-400/10 border border-emerald-400/30">
                <Check className="w-4.5 h-4.5 text-emerald-400" />
              </span>
              <div>
                <p className="text-sm font-secondary font-bold text-white">On track</p>
                <p className="text-xs text-zinc-500">You've been following through — nice.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── 30-day COM-B pattern — %-split mini-bar + AI insight (Phase 5) ── */}
      {hasPattern && (
        <div className="mt-4 pt-3 border-t border-zinc-800" data-testid="behavior-checkin-pattern">
          <p className="text-[10px] uppercase tracking-wider text-amber-300/80 mb-1.5">Last 30 days · why you missed</p>
          {patternV2?.split ? (
            <>
              <div className="flex h-2 rounded-full overflow-hidden bg-zinc-800 mb-1.5" data-testid="behavior-split-bar">
                {['capability', 'opportunity', 'motivation'].map(k => (
                  (patternV2.split[k] || 0) > 0 ? (
                    <div key={k} style={{ width: `${patternV2.split[k]}%`, background: SPLIT_META[k].color }} title={`${SPLIT_META[k].label} ${patternV2.split[k]}%`} />
                  ) : null
                ))}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mb-2">
                {['capability', 'opportunity', 'motivation'].map(k => (
                  <span key={k} className="inline-flex items-center gap-1 text-[10px] text-zinc-400" data-testid={`behavior-split-${k}`}>
                    <span className="w-2 h-2 rounded-full" style={{ background: SPLIT_META[k].color }} />
                    {SPLIT_META[k].label} {patternV2.split[k] || 0}%
                  </span>
                ))}
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed border-l-2 border-amber-400/40 pl-2.5" data-testid="behavior-ai-insight">
                {patternV2.ai_insight}
              </p>
            </>
          ) : (
            <p className="text-xs text-zinc-400">
              Your main blocker lately has been{' '}
              <span className="text-amber-200 font-semibold">{pattern.top_label}</span>.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default BehaviorCheckinCard;
